import { useState } from "react";
import api from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const tl = (v) => (v == null || v === "" ? "—" : `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v))} TL`);

function Line({ label, children, testId }) {
  return (
    <div className="flex justify-between items-center gap-4 py-2 border-b border-slate-100 last:border-0">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-sm text-slate-900 text-right" data-testid={testId}>{children}</span>
    </div>
  );
}

export function RenewalSettingsCard({ company, onUpdated }) {
  const rs = company.renewal_settings || {};
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const priceDiffers = rs.previous_amount_tl != null && rs.current_plan_amount_tl != null
    && Math.abs(Number(rs.previous_amount_tl) - Number(rs.current_plan_amount_tl)) >= 0.005;

  const patch = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/companies/${company.id}/renewal-settings`, body);
      onUpdated(data);
      toast.success("Yenileme ayarı kaydedildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };

  const sendNotice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${company.id}/price-change-notice`);
      onUpdated(data);
      toast.success("Fiyat değişikliği bildirimi gönderildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bildirim gönderilemedi");
    } finally { setBusy(false); setConfirmOpen(false); }
  };

  return (
    <SectionCard title="Otomatik Yenileme">
      <div className="p-5 space-y-4" data-testid="admin-renewal-settings">
        <label className="flex items-start justify-between gap-4 cursor-pointer">
          <span>
            <span className="block text-sm font-medium text-slate-900">İç test hesabı — otomatik tahsilat kapalı</span>
            <span className="block text-xs text-slate-500">Açıkken bu firmadan hiçbir otomatik yenileme tahsilatı yapılmaz.</span>
          </span>
          <Switch checked={!!rs.renewal_exempt} disabled={busy}
            onCheckedChange={(v) => patch({ renewal_exempt: v })} data-testid="admin-renewal-exempt-switch" />
        </label>
        <label className="flex items-start justify-between gap-4 cursor-pointer">
          <span>
            <span className="block text-sm font-medium text-slate-900">Fiyat bildirimi şartı aranmasın (iç hesap)</span>
            <span className="block text-xs text-slate-500">Açıkken yenilemede 30 günlük bildirim beklenmeden güncel plan fiyatı çekilir.</span>
          </span>
          <Switch checked={!!rs.price_notice_exempt} disabled={busy}
            onCheckedChange={(v) => patch({ price_notice_exempt: v })} data-testid="admin-price-notice-exempt-switch" />
        </label>
        <div>
          <Line label="Önceki Geçerli Tutar" testId="admin-renewal-previous-amount">{tl(rs.previous_amount_tl)}</Line>
          <Line label="Güncel Plan Tutarı" testId="admin-renewal-current-amount">{tl(rs.current_plan_amount_tl)}</Line>
          <Line label="Fiyat Bildirimi" testId="admin-renewal-notified-at">{rs.price_change_notified_at ? fmtDateTime(rs.price_change_notified_at) : "—"}</Line>
          <Line label="Yeni Fiyat Geçerlilik" testId="admin-renewal-effective-from">{rs.price_notice_effective_from ? fmtDate(rs.price_notice_effective_from) : "—"}</Line>
        </div>
        <Button variant="outline" className="w-full h-10 rounded-lg" disabled={busy || !priceDiffers}
          onClick={() => setConfirmOpen(true)} data-testid="admin-price-change-notice-btn">
          Fiyat değişikliği bildir
        </Button>
        {!priceDiffers && (
          <div className="text-xs text-slate-500">
            {rs.current_plan_amount_tl == null ? "Firmanın aylık/yıllık ücretli planı yok." : "Firmanın fiyatı güncel plan fiyatıyla aynı; bildirim gerekmez."}
          </div>
        )}
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="admin-price-notice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Fiyat değişikliği bildirilsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Firma yetkilisine gerçek bir e-posta gönderilir. {tl(rs.previous_amount_tl)} yerine {tl(rs.current_plan_amount_tl)} tutarı, bildirimden 30 gün sonraki ilk yenilemede uygulanır.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-price-notice-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={sendNotice} data-testid="admin-price-notice-confirm">Gönder</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}

export default RenewalSettingsCard;
